import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { twoFactorService } from '../../services/twoFactorService';
import { useAuth } from '../../contexts/AuthContext';
import StatusOverview from './components/StatusOverview';
import MethodsPanel from './components/MethodsPanel';
import TrustedDevicesPanel from './components/TrustedDevicesPanel';
import SecurityActivityFeed from './components/SecurityActivityFeed';
import BackupCodesPanel from './components/BackupCodesPanel';
import BiometricPanel from './components/BiometricPanel';

export default function TwoFactorAuthenticationManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [methods, setMethods] = useState([]);
  const [devices, setDevices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [backupCodesCount, setBackupCodesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statusData, methodsData, devicesData, activityData, codesCount] = await Promise.all([
        twoFactorService?.get2FAStatus(),
        twoFactorService?.getMethods(),
        twoFactorService?.getTrustedDevices(),
        twoFactorService?.getSecurityActivity(20),
        twoFactorService?.getBackupCodesCount()
      ]);

      setStatus(statusData);
      setMethods(methodsData);
      setDevices(devicesData);
      setActivity(activityData);
      setBackupCodesCount(codesCount);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMethod = async (methodId, isEnabled) => {
    try {
      await twoFactorService?.toggleMethod(methodId, isEnabled);
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleRemoveMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to remove this authentication method?')) {
      return;
    }
    try {
      await twoFactorService?.removeMethod(methodId);
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleRevokeDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to revoke this trusted device?')) {
      return;
    }
    try {
      await twoFactorService?.revokeDevice(deviceId);
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!window.confirm('This will invalidate all existing backup codes. Continue?')) {
      return;
    }
    try {
      const newCodes = await twoFactorService?.generateBackupCodes(10);
      // Show codes in a modal or download them
      const content = `TriRail StablePay - New Backup Codes\n\nGenerated: ${new Date()?.toLocaleString()}\n\n${newCodes?.join('\n')}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trirail-backup-codes-${Date.now()}.txt`;
      a?.click();
      URL.revokeObjectURL(url);
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleAddMethod = () => {
    navigate('/two-factor-authentication-setup');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Two-Factor Authentication Management</h1>
          <p className="text-lg text-gray-600">Manage your authentication methods, trusted devices, and security settings</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Status Overview */}
        <StatusOverview status={status} onAddMethod={handleAddMethod} />

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Authentication Methods */}
          <MethodsPanel
            methods={methods}
            onToggle={handleToggleMethod}
            onRemove={handleRemoveMethod}
            onAddMethod={handleAddMethod}
          />

          {/* Backup Codes */}
          <BackupCodesPanel
            count={backupCodesCount}
            onRegenerate={handleRegenerateBackupCodes}
          />
        </div>

        {/* Biometric Authentication */}
        <div className="mb-6">
          <BiometricPanel onRefresh={loadData} />
        </div>

        {/* Trusted Devices */}
        <TrustedDevicesPanel
          devices={devices}
          onRevoke={handleRevokeDevice}
        />

        {/* Security Activity Feed */}
        <SecurityActivityFeed activity={activity} />
      </div>
    </div>
  );
}